import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ArtistProductEnquiryService } from '../artist-product-enquiry.service';
import { ProductRequestQueueService } from '../product-request-queue.service';
import { ConnectionsService } from '../connections.service';

/** What kind of thing happened — drives the icon and where clicking it goes. */
export type ActivityKind = 'enquiry' | 'product-request' | 'connection-request';

/**
 * One thing that needs the artist's attention, normalised across sources so a single list can
 * show them together, newest first.
 */
export interface ActivityItem {
    kind: ActivityKind;
    /** Headline — the piece, the gallery, the person. */
    title: string;
    /** Supporting line: who it came from, or what they said. */
    detail: string;
    /** When it happened. Sorting depends on this being a real date. */
    occurredAt: Date | null;
    unread: boolean;
    /** Where clicking should land. */
    link: any[];
    icon: string;
}

/**
 * The artist's attention feed: enquiries, gallery product requests and connection requests in one
 * ordered list.
 *
 * ── Why this is a fan-out, and why that is temporary ──────────────────────────────
 * There is no aggregated endpoint. Each source is fetched separately and merged here, which is
 * three requests and no shared read/unread state or cross-source paging. That is a known
 * limitation, logged as DW-016 (the unified Inbox feed, which needs a backend aggregation
 * endpoint).
 *
 * The point of this service is that it is the ONLY place that knows. Callers ask for
 * {@link getFeed} and get `ActivityItem[]`. When the aggregated endpoint lands, this file changes
 * and nothing else does — which is why the dashboard should not be calling three services itself.
 *
 * A source that fails yields an empty list rather than failing the feed: a dashboard missing one
 * of three strands is far better than a dashboard showing an error.
 */
@Injectable({
    providedIn: 'root'
})
export class ActivityFeedService {

    constructor(
        private enquiryService: ArtistProductEnquiryService,
        private productRequestQueueService: ProductRequestQueueService,
        private connectionsService: ConnectionsService,
    ) { }

    /**
     * @param artistId the signed-in artist — needed by the connections call, which has no
     *                 "mine" variant and takes the id explicitly.
     */
    public getFeed(artistId: string, limit = 8): Observable<ActivityItem[]> {
        return forkJoin({
            enquiries: this.enquiryService.getMyEnquiries().pipe(catchError(() => of(null))),
            requests: this.productRequestQueueService.getAllProductRequests().pipe(catchError(() => of([]))),
            // There is no endpoint for "pending invitations" — the connection-requests screen asks
            // for connected galleries including invited ones and filters client-side. Same call and
            // same filter here, so the feed cannot disagree with the screen it links to.
            connections: this.connectionsService.getConnectedGalleries(artistId, true).pipe(catchError(() => of([]))),
        }).pipe(
            map(({ enquiries, requests, connections }) => {
                const items: ActivityItem[] = [
                    ...this.fromEnquiries(enquiries),
                    ...this.fromProductRequests(requests),
                    ...this.fromConnections(connections),
                ];

                return items
                    // Undated items sink rather than jumping to the top of the list.
                    .sort((a, b) => (b.occurredAt ? b.occurredAt.getTime() : 0) - (a.occurredAt ? a.occurredAt.getTime() : 0))
                    .slice(0, limit);
            })
        );
    }

    private fromEnquiries(res: any): ActivityItem[] {
        const rows = res && res.Success && Array.isArray(res.ResponseData) ? res.ResponseData : [];
        return rows.map((e: any) => ({
            kind: 'enquiry' as ActivityKind,
            title: e.ArtistProductName || 'Website enquiry',
            detail: `${e.SenderName || 'Someone'} — ${this.firstLine(e.Message)}`,
            occurredAt: this.toDate(e.CreatedDate),
            unread: !e.ReadDate,
            link: ['/artists', 'enquiries'],
            icon: 'fa fa-envelope',
        }));
    }

    private fromProductRequests(rows: any): ActivityItem[] {
        const list = Array.isArray(rows) ? rows : [];
        return list
            // Answered requests are history, not attention.
            .filter((r: any) => !r.IsProductAccepted && !r.IsProductRejected)
            .map((r: any) => ({
                kind: 'product-request' as ActivityKind,
                title: r.ProductName || 'Product request',
                detail: `${r.BusinessName || r.GalleryName || 'A gallery'} would like this piece`,
                occurredAt: this.toDate(r.RequestedDate),
                unread: true,
                link: ['/artists', 'product-requests'],
                icon: 'fa fa-university',
            }));
    }

    private fromConnections(rows: any): ActivityItem[] {
        const list = Array.isArray(rows) ? rows : [];
        return list
            // Incoming only: an invitation the gallery sent that the artist has not accepted.
            // Mirrors ConnectionRequestsComponent exactly.
            .filter((c: any) => !c.IsInviteAccepted && !c.IsInitiatedByArtist)
            .map((c: any) => ({
                kind: 'connection-request' as ActivityKind,
                title: c.BusinessName || c.GalleryName || c.Name || 'A gallery',
                detail: 'Would like to connect with you',
                occurredAt: this.toDate(c.CreatedDate || c.InvitedDate || c.RequestedDate),
                unread: true,
                link: ['/connection-requests'],
                icon: 'fa fa-link',
            }));
    }

    /** Messages are shown as a single line in the feed; the full text lives on the detail screen. */
    private firstLine(message: string): string {
        const flat = (message || '').replace(/\s+/g, ' ').trim();
        return flat.length > 90 ? flat.slice(0, 90) + '…' : flat;
    }

    private toDate(value: any): Date | null {
        if (!value) { return null; }
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
}
